import type { MentionContext, MentionResolver, ResolvedMention } from '../types'

type VideoRow = {
  id: string
  title: string | null
  status: string
  thumbnail_url: string | null
  thumbnail_sm_url: string | null
  campaign_id: string | null
}

function scoreVideo(title: string, query: string): number {
  const n = title.toLowerCase()
  const q = query.toLowerCase()
  if (!q) return 50
  if (n === q) return 100
  if (n.startsWith(q)) return 80 - (n.length - q.length)
  if (n.includes(q)) return 55
  // Subsequence fuzzy match — matches scoreCampaign's behavior so the
  // ranking feels consistent across kinds.
  let qi = 0
  for (const ch of n) {
    if (ch === q[qi]) qi++
    if (qi === q.length) break
  }
  if (qi === q.length) return 30 - (n.length - q.length)
  return 0
}

function makeSubtitle(row: VideoRow): string {
  // The video's status carries useful signal in chat (e.g. "rendering",
  // "completed", "failed"). Surfacing it here helps the agent pick the
  // right video when the user has multiple with similar titles.
  return row.status
}

export const videoResolver: MentionResolver = async (query, ctx: MentionContext) => {
  // Cap at 200 to mirror the campaign resolver. Untitled videos are
  // filtered out — there's nothing to fuzzy-match against and they're
  // not user-facing anyway.
  const { data, error } = await ctx.supabase
    .from('video_generation_jobs')
    .select('id, title, status, thumbnail_url, thumbnail_sm_url, campaign_id')
    .eq('workspace_id', ctx.workspaceId)
    .order('updated_at', { ascending: false })
    .limit(200)

  if (error) return { ok: false, error: `Could not load videos: ${error.message}` }

  const rows = ((data ?? []) as VideoRow[]).filter((r) => r.title && r.title.trim().length > 0)
  if (rows.length === 0) return { ok: true, candidates: [] }

  const scored = rows
    .map((row) => ({ row, score: scoreVideo(row.title as string, query) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || (a.row.title || '').localeCompare(b.row.title || ''))
    .slice(0, 8)

  const candidates: ResolvedMention[] = scored.map(({ row }) => ({
    kind: 'video',
    id: row.id,
    display: row.title as string,
    subtitle: makeSubtitle(row),
    href: `/videos/${row.id}`,
    data: {
      id: row.id,
      title: row.title,
      status: row.status,
      thumbnail_url: row.thumbnail_url,
      campaign_id: row.campaign_id,
    },
  }))

  return { ok: true, candidates }
}
